<template>
  <div class="device-params-page">
    <div class="device-params-toolbar d-flex flex-wrap align-center justify-space-between ga-3 mb-4">
      <v-select
        v-model="selectedElink"
        :items="elinkItems"
        item-title="title"
        item-value="value"
        label="eLink"
        variant="outlined"
        density="comfortable"
        hide-details
        rounded="lg"
        class="device-params-toolbar__select"
      />
      <v-btn variant="text" color="primary" class="text-none" prepend-icon="mdi-playlist-edit" @click="openCommandHistory">
        命令记录
      </v-btn>
    </div>

    <v-card variant="outlined" rounded="lg" class="device-params-card gm-card">
      <v-card-title class="text-subtitle-1 font-weight-semibold pb-0">参数设置</v-card-title>
      <v-card-text class="pt-4">
        <v-row class="params-split" align="stretch">
          <v-col cols="12" lg="5">
            <div class="param-rows">
              <div class="param-row d-flex align-center flex-wrap ga-2 py-3">
                <span class="param-row__label text-body-2 text-medium-emphasis">输变容量配比</span>
                <span class="param-row__value text-body-1 font-weight-medium">{{ tfRatioDisplay }}</span>
                <v-btn variant="text" color="primary" size="small" class="text-none px-1" @click="openEdit('tf')">
                  修改
                </v-btn>
              </div>
              <v-divider class="opacity-60" />
              <div class="param-row d-flex align-center flex-wrap ga-2 py-3">
                <span class="param-row__label text-body-2 text-medium-emphasis">最小需量</span>
                <span class="param-row__value text-body-1 font-weight-medium">{{ minDemandDisplay }}</span>
                <v-btn variant="text" color="primary" size="small" class="text-none px-1" @click="openEdit('demand')">
                  修改
                </v-btn>
              </div>
              <v-divider class="opacity-60" />
              <div class="param-row d-flex align-center flex-wrap ga-2 py-3">
                <span class="param-row__label text-body-2 text-medium-emphasis">无功工作模式</span>
                <span class="param-row__value text-body-1 font-weight-medium">{{ reactiveModeLabel }}</span>
                <v-btn variant="text" color="primary" size="small" class="text-none px-1" @click="openEdit('reactive')">
                  修改
                </v-btn>
              </div>
            </div>
          </v-col>

          <v-col cols="12" lg="7">
            <div class="protocol-panel rounded-lg pa-4">
              <div class="text-body-2 font-weight-medium mb-2">设置协议帧</div>
              <v-textarea
                v-model="protocolFrame"
                placeholder="请输入协议帧"
                variant="outlined"
                rows="8"
                hide-details
                rounded="lg"
                class="protocol-panel__textarea"
              />
              <div class="d-flex justify-end mt-3">
                <v-btn color="primary" variant="flat" rounded="lg" class="text-none" @click="onSendFrame">发送</v-btn>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card variant="outlined" rounded="lg" class="device-params-card gm-card mt-4">
      <v-card-title class="text-subtitle-1 font-weight-semibold pb-0">eBlock启停</v-card-title>
      <v-card-text class="pt-4">
        <div class="eblock-row d-flex flex-wrap ga-3">
          <div
            v-for="eb in eblocks"
            :key="eb.id"
            class="eblock-card rounded-lg pa-3 d-flex flex-column ga-2"
          >
            <div class="d-flex align-center ga-2 flex-wrap">
              <span class="text-body-2 font-weight-medium">{{ eb.label }}</span>
              <span
                class="eblock-card__dot"
                :class="eb.online ? 'eblock-card__dot--on' : 'eblock-card__dot--off'"
                :title="eb.online ? '在线' : '离线'"
              />
            </div>
            <div class="d-flex ga-2 flex-wrap">
              <v-btn
                class="eblock-btn eblock-btn--enable text-none"
                variant="flat"
                rounded="lg"
                size="small"
                @click="setEblockState(eb, 'enabled')"
              >
                启用
              </v-btn>
              <v-btn
                class="eblock-btn eblock-btn--disable text-none"
                variant="flat"
                rounded="lg"
                size="small"
                @click="setEblockState(eb, 'disabled')"
              >
                停用
              </v-btn>
            </div>
            <div v-if="eb.runState" class="text-caption text-medium-emphasis">
              当前：{{ eb.runState === 'enabled' ? '已启用' : '已停用' }}
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- 修改参数 -->
    <v-dialog v-model="editDialog.open" max-width="420" scrollable>
      <v-card rounded="lg">
        <v-card-title class="text-subtitle-1">{{ editDialog.title }}</v-card-title>
        <v-card-text class="pt-2">
          <v-text-field
            v-if="editDialog.field === 'tf'"
            v-model.number="editDialog.num"
            label="输变容量配比"
            type="number"
            step="0.01"
            variant="outlined"
            density="comfortable"
            hide-details="auto"
            rounded="lg"
          />
          <v-text-field
            v-else-if="editDialog.field === 'demand'"
            v-model.number="editDialog.num"
            label="最小需量(kW)"
            type="number"
            variant="outlined"
            density="comfortable"
            hide-details="auto"
            rounded="lg"
          />
          <v-select
            v-else-if="editDialog.field === 'reactive'"
            v-model="editDialog.reactive"
            :items="reactiveItems"
            item-title="title"
            item-value="value"
            label="无功工作模式"
            variant="outlined"
            density="comfortable"
            hide-details="auto"
            rounded="lg"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" rounded="lg" class="text-none" @click="editDialog.open = false">取消</v-btn>
          <v-btn color="primary" variant="flat" rounded="lg" class="text-none" @click="confirmEdit">确定</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 命令记录 -->
    <v-dialog v-model="historyOpen" max-width="1180" scrollable content-class="command-history-dialog">
      <v-card rounded="lg" class="command-history-card">
        <v-card-title class="d-flex align-center command-history-border-b">
          <span class="text-h6 font-weight-semibold">命令记录</span>
          <v-spacer />
          <v-btn icon variant="text" density="comfortable" aria-label="关闭" @click="historyOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle class="text-wrap py-3 command-history-border-b text-body-2">
          当前 eLink：<strong>{{ currentElinkTitle }}</strong> · 演示数据
        </v-card-subtitle>
        <v-card-text class="pa-0">
          <v-data-table
            :headers="commandHistoryHeaders"
            :items="commandLogPagedRows"
            item-value="id"
            class="command-history-table"
            fixed-header
            height="420"
            hover
            hide-default-footer
            :row-props="commandHistoryRowProps"
          >
            <template #item.sendContent="{ item }">
              <div class="command-cell command-cell--send">
                <div><span class="command-cell__k">发送时间</span> {{ item.sendTime }}</div>
                <div class="mt-1">
                  <span class="command-cell__k">协议帧</span>
                  <span class="command-cell__hex">{{ item.protocolFrame }}</span>
                </div>
              </div>
            </template>
            <template #item.recvContent="{ item }">
              <div class="command-cell command-cell--recv">
                <div><span class="command-cell__k">接收时间</span> {{ item.recvTime }}</div>
                <div class="mt-1 command-cell__kv">
                  <span>最大需量：{{ item.maxDemand }}</span>
                  <span>最小需量：{{ item.minDemand }}</span>
                </div>
                <div class="mt-1 command-cell__kv">
                  <span>箱变容量配比：{{ item.tfRatio }}</span>
                  <span>无功工作模式：{{ item.reactiveModeText }}</span>
                </div>
              </div>
            </template>
          </v-data-table>
          <div class="command-history-footer d-flex flex-wrap align-center justify-end ga-4 pa-3 command-history-border-t">
            <span class="text-body-2 text-medium-emphasis">共 {{ commandLogRows.length }} 条</span>
            <v-select
              v-model="commandHistoryItemsPerPage"
              :items="commandHistoryPageSizeOptions"
              item-title="title"
              item-value="value"
              density="compact"
              variant="outlined"
              hide-details
              class="command-history-footer__page-size"
              @update:model-value="onCommandHistoryPageSizeChange"
            />
            <v-pagination
              v-model="commandHistoryPage"
              :length="commandHistoryPageCount"
              :total-visible="7"
              density="comfortable"
              rounded="lg"
              class="command-history-footer__pager"
            />
            <div class="d-flex align-center ga-2 text-body-2">
              <span class="text-medium-emphasis">前往</span>
              <v-text-field
                v-model.number="commandHistoryJumpPage"
                type="number"
                density="compact"
                variant="outlined"
                hide-details
                class="command-history-footer__jump"
                @keyup.enter="applyCommandHistoryJump"
              />
              <span class="text-medium-emphasis">页</span>
              <v-btn size="small" variant="tonal" rounded="lg" class="text-none" @click="applyCommandHistoryJump">
                跳转
              </v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, reactive, watch } from 'vue'
import { eui } from 'ems-ui'

const COMMAND_LOG_TOTAL = 47

const PROTO_FRAMES = [
  '0106F0F100F000B80002DC28D55A',
  '0106F0F100F000B80002DC28D55B',
  '0106F0F100F000B80002DC28D55C',
  '0106F0F100F000B80002DC28D55D',
  '0106F0F100F000B80002DC28D55E',
]

const OPERATORS = ['15309344450', '15191194106']

function buildCommandLogRows() {
  const rows = []
  for (let i = 0; i < COMMAND_LOG_TOTAL; i++) {
    const day = 15 - (i % 10)
    const h = 9 + (i % 10)
    const m = 10 + (i % 49)
    const sendTime = `2026-01-${String(Math.max(1, day)).padStart(2, '0')} ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:07`
    rows.push({
      id: i,
      uuid: `A2211E20S${String(331 + (i % 180)).padStart(4, '0')}`,
      sendTime,
      protocolFrame: PROTO_FRAMES[i % PROTO_FRAMES.length],
      recvTime: sendTime,
      maxDemand: 2500 - (i % 6) * 15,
      minDemand: 10 + (i % 3),
      tfRatio: (0.85 + (i % 8) * 0.01).toFixed(2),
      reactiveModeText: i % 5 === 0 ? '不出无功' : '功率因数补偿',
      operator: OPERATORS[i % OPERATORS.length],
    })
  }
  return rows
}

const elinkItems = [
  { title: '1-1#eLink', value: '1-1' },
  { title: '1-2#eLink', value: '1-2' },
  { title: '2-1#eLink', value: '2-1' },
]

const selectedElink = ref('1-1')

const transformerRatio = ref(0.9)
const minDemandKw = ref(10)
const reactiveMode = ref('none')

const reactiveItems = [
  { title: '不出无功', value: 'none' },
  { title: '优先出无功', value: 'pfc' },
  { title: '优先出有功', value: 'pfp' },
]

const protocolFrame = ref('')

const eblocks = ref([
  { id: '1-1-1', label: '1-1-1#eBlock', online: true, runState: null },
  { id: '1-1-2', label: '1-1-2#eBlock', online: true, runState: 'enabled' },
  { id: '1-1-3', label: '1-1-3#eBlock', online: true, runState: null },
  { id: '1-1-4', label: '1-1-4#eBlock', online: true, runState: null },
  { id: '1-1-5', label: '1-1-5#eBlock', online: false, runState: null },
  { id: '1-1-6', label: '1-1-6#eBlock', online: true, runState: 'disabled' },
])

const tfRatioDisplay = computed(() => transformerRatio.value.toFixed(2))
const minDemandDisplay = computed(() => `${minDemandKw.value} kW`)
const reactiveModeLabel = computed(() => {
  const it = reactiveItems.find((x) => x.value === reactiveMode.value)
  return it?.title ?? '—'
})

const currentElinkTitle = computed(() => elinkItems.find((x) => x.value === selectedElink.value)?.title ?? selectedElink.value)

const commandLogRows = ref(buildCommandLogRows())

const commandHistoryHeaders = [
  { title: 'UUID', key: 'uuid', width: 148, sortable: false },
  { title: '发送内容', key: 'sendContent', sortable: false, minWidth: 260 },
  { title: '接收内容', key: 'recvContent', sortable: false, minWidth: 280 },
  { title: '操作者', key: 'operator', width: 132, sortable: false },
]

const commandHistoryPage = ref(1)
const commandHistoryItemsPerPage = ref(10)
const commandHistoryPageSizeOptions = [
  { title: '10条/页', value: 10 },
  { title: '20条/页', value: 20 },
  { title: '50条/页', value: 50 },
]
const commandHistoryJumpPage = ref(1)

const commandHistoryPageCount = computed(() => {
  const n = commandLogRows.value.length
  const per = commandHistoryItemsPerPage.value
  return Math.max(1, Math.ceil(n / per))
})

const commandLogPagedRows = computed(() => {
  const start = (commandHistoryPage.value - 1) * commandHistoryItemsPerPage.value
  return commandLogRows.value.slice(start, start + commandHistoryItemsPerPage.value)
})

function commandHistoryRowProps(data) {
  const raw = data.item?.raw ?? data.item
  const id = raw?.id
  const highlight = commandHistoryPage.value === 1 && id === 0
  return {
    class: highlight ? 'command-history-row--highlight' : '',
  }
}

function applyCommandHistoryJump() {
  let p = Math.floor(Number(commandHistoryJumpPage.value))
  if (!Number.isFinite(p)) p = 1
  p = Math.min(Math.max(1, p), commandHistoryPageCount.value)
  commandHistoryPage.value = p
  commandHistoryJumpPage.value = p
}

const historyOpen = ref(false)

watch(commandHistoryPage, (p) => {
  commandHistoryJumpPage.value = p
})

watch(historyOpen, (open) => {
  if (open) {
    commandHistoryPage.value = 1
    commandHistoryJumpPage.value = 1
  }
})

watch(commandHistoryPageCount, (max) => {
  if (commandHistoryPage.value > max) {
    commandHistoryPage.value = max
  }
})

function onCommandHistoryPageSizeChange() {
  commandHistoryPage.value = 1
}

const editDialog = reactive({
  open: false,
  field: '',
  title: '',
  num: 0,
  reactive: 'none',
})

function openEdit(field) {
  editDialog.field = field
  if (field === 'tf') {
    editDialog.title = '修改输变容量配比'
    editDialog.num = transformerRatio.value
  } else if (field === 'demand') {
    editDialog.title = '修改最小需量'
    editDialog.num = minDemandKw.value
  } else if (field === 'reactive') {
    editDialog.title = '修改无功工作模式'
    editDialog.reactive = reactiveMode.value
  }
  editDialog.open = true
}

function confirmEdit() {
  if (editDialog.field === 'tf') {
    const v = Number(editDialog.num)
    transformerRatio.value = Number.isFinite(v) ? Math.min(2, Math.max(0, Math.round(v * 100) / 100)) : transformerRatio.value
  } else if (editDialog.field === 'demand') {
    const v = Number(editDialog.num)
    minDemandKw.value = Number.isFinite(v) ? Math.max(0, v) : minDemandKw.value
  } else if (editDialog.field === 'reactive') {
    reactiveMode.value = editDialog.reactive
  }
  editDialog.open = false
  eui.showToast('参数已更新（本地草稿）', 'success')
}

function onSendFrame() {
  if (!protocolFrame.value?.trim()) {
    eui.showToast('请先输入协议帧内容', 'info')
    return
  }
  eui.showToast('演示：协议帧已加入发送队列（未调用网关）', 'success')
}

function setEblockState(eb, state) {
  eb.runState = state
  eui.showToast(`演示：${eb.label} 已设为「${state === 'enabled' ? '启用' : '停用'}」`, 'success')
}

function openCommandHistory() {
  historyOpen.value = true
}

defineExpose({
  getDraft: () => ({
    panel: 'device_run_params',
    selectedElink: selectedElink.value,
    transformerCapacityRatio: transformerRatio.value,
    minDemandKw: minDemandKw.value,
    reactivePowerMode: reactiveMode.value,
    protocolFrame: protocolFrame.value,
    eblockRunStates: eblocks.value.map((eb) => ({
      id: eb.id,
      label: eb.label,
      online: eb.online,
      runState: eb.runState,
    })),
  }),
})
</script>

<style scoped lang="scss">
.device-params-page {
  max-width: 1100px;
}

.device-params-toolbar__select {
  min-width: 200px;
  max-width: 280px;
}

.device-params-card {
  border-color: #e9efff !important;
  background: #fff;
}

.param-rows {
  border: 1px solid #edf2f6;
  border-radius: 10px;
  padding: 4px 16px;
  background: #fafbff;
}

.param-row__label {
  min-width: 112px;
}

.param-row__value {
  color: var(--color-text, #444a6d);
  min-width: 72px;
}

.protocol-panel {
  border: 1px solid #e9efff;
  background: #fafbff;
  min-height: 100%;
}

.protocol-panel__textarea :deep(textarea) {
  font-family: ui-monospace, 'Cascadia Code', 'Consolas', monospace;
  font-size: 13px;
  line-height: 1.45;
}

.params-split {
  --v-gutter-x: 24px;
}

.eblock-card {
  min-width: 168px;
  border: 1px solid #e9efff;
  background: #fff;
  box-shadow: 0 1px 4px rgba(8, 40, 87, 0.04);
}

.eblock-card__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  &--on {
    background: var(--color-success, #36c070);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--color-success, #36c070) 25%, transparent);
  }
  &--off {
    background: #bdbdbd;
  }
}

.eblock-btn {
  letter-spacing: 0;
  min-width: 72px;
  font-weight: 600;
}

.eblock-btn--enable {
  background: #e8f5e9 !important;
  color: #2e7d32 !important;
}

.eblock-btn--disable {
  background: #ffebee !important;
  color: #c62828 !important;
}

.command-history-border-b {
  border-bottom: 1px solid #edf2f6;
}

.command-history-border-t {
  border-top: 1px solid #edf2f6;
}

.command-history-card {
  overflow: hidden;
}

.command-history-table {
  border-bottom: 1px solid #edf2f6;
}

.command-history-table :deep(.v-data-table__th) {
  font-weight: 600 !important;
  font-size: 13px !important;
  color: var(--color-text, #444a6d) !important;
  background: #f4f6fc !important;
}

.command-history-table :deep(tr.command-history-row--highlight) {
  background: #e3f2fd !important;
}

.command-history-table :deep(tr.command-history-row--highlight:hover) {
  background: #d4eafd !important;
}

.command-cell {
  font-size: 13px;
  line-height: 1.45;
  color: var(--color-text, #444a6d);
  padding: 4px 0;
}

.command-cell__k {
  font-weight: 600;
  color: #5c6378;
  margin-right: 4px;
}

.command-cell__hex {
  font-family: ui-monospace, 'Cascadia Code', 'Consolas', monospace;
  font-size: 12px;
  word-break: break-all;
}

.command-cell__kv {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
}

.command-history-footer {
  background: #fafbff;
}

.command-history-footer__page-size {
  max-width: 140px;
  min-width: 120px;
}

.command-history-footer__jump {
  max-width: 80px;
}

.command-history-footer__pager :deep(.v-pagination__item) {
  min-width: 34px;
}
</style>
